// Voice — Phase 4: three worklet oscillators -> per-osc level -> sum -> filter
// -> VCA. Worklet oscillators run continuously and are gated by the amp ADSR on
// the VCA (so there is no per-note node churn and glide is just a frequency
// ramp). Per-osc drive + analyser taps are inserted in Phase 5.
//
// All three oscillator worklets share the same AudioParam interface —
// frequency (Hz, post-octave), detune (cents), shape (0..1) — so this class
// treats the slots uniformly regardless of synthesis type.

import { midiToFreq } from '../lib/midi-note';
import { ADSR } from './envelopes/ADSR';
import { DriveStage } from './Mixer';
import { getDefaultPatch } from './presets';
import type { OscSlotConfig } from './oscillators/load';
import type { ParamId } from './params';

const VOICE_PEAK = 0.3; // headroom for summing voices (staging refined Phase 5/12)
const FILTER_ENV_HZ = 6000;

const Q_MIN = 0.5;
const Q_MAX = 20;
export function resonanceToQ(r: number): number {
  return Q_MIN + (Q_MAX - Q_MIN) * Math.min(1, Math.max(0, r));
}
export const Q_RANGE = Q_MAX - Q_MIN;

export type OscIndex = 0 | 1 | 2;
const OSC_PREFIX = ['osc1', 'osc2', 'osc3'] as const;

interface OscSlot {
  node: AudioWorkletNode;
  drive: DriveStage;
  level: GainNode;
  octave: number;
}

export class Voice {
  private readonly ctx: BaseAudioContext;
  private readonly sum: GainNode;
  private readonly filter: BiquadFilterNode;
  private readonly vca: GainNode;

  private readonly ampEnv = new ADSR();
  private readonly filterEnv = new ADSR({ attack: 0.005, decay: 0.4, sustain: 0.3 });
  private readonly filterEnvSource: ConstantSourceNode;
  private readonly filterEnvAmount: GainNode;

  private readonly oscs: (OscSlot | null)[] = [null, null, null];
  private current: number | null = null;

  /**
   * @param oscSlots per-slot worklet config (`null` leaves a slot silent).
   * @param analysers shared per-osc analyser taps (one per slot, scope display).
   */
  constructor(
    ctx: BaseAudioContext,
    destination: AudioNode,
    oscSlots: readonly (OscSlotConfig | null)[],
    analysers: readonly (AnalyserNode | null)[] = [],
  ) {
    this.ctx = ctx;
    const defaults = getDefaultPatch();

    this.sum = ctx.createGain();
    this.sum.gain.value = 1;

    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = defaults['filter.cutoff'] as number;
    this.filter.Q.value = resonanceToQ(defaults['filter.resonance'] as number);

    this.vca = ctx.createGain();
    this.vca.gain.value = 0;

    this.filterEnvSource = ctx.createConstantSource();
    this.filterEnvSource.offset.value = 0;
    this.filterEnvAmount = ctx.createGain();
    this.filterEnvAmount.gain.value = 0;
    this.filterEnvSource.connect(this.filterEnvAmount);
    this.filterEnvAmount.connect(this.filter.frequency);
    this.filterEnvSource.start();

    this.sum.connect(this.filter);
    this.filter.connect(this.vca);
    this.vca.connect(destination);

    for (let i = 0; i < 3; i++) {
      const cfg = oscSlots[i] ?? null;
      if (!cfg) continue;
      const prefix = OSC_PREFIX[i as OscIndex];
      const node = new AudioWorkletNode(ctx as AudioContext, cfg.name, {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        ...(cfg.processorOptions ? { processorOptions: cfg.processorOptions } : {}),
      });
      const drive = new DriveStage(ctx);
      drive.setDrive(defaults[`${prefix}.drive`] as number);
      const level = ctx.createGain();
      level.gain.value = defaults[`${prefix}.level`] as number;

      // osc -> drive -> level -> sum, with a pre-sum analyser tap for the scope.
      node.connect(drive.node);
      drive.node.connect(level);
      level.connect(this.sum);
      const analyser = analysers[i] ?? null;
      if (analyser) level.connect(analyser);

      this.oscs[i] = {
        node,
        drive,
        level,
        octave: defaults[`${prefix}.octave`] as number,
      };
    }
  }

  get note(): number | null {
    return this.current;
  }

  /** Trigger a note. With `glide > 0` and a note already sounding, the pitch
   *  ramps from the current note; envelopes always (re)trigger. */
  noteOn(midi: number, when = this.ctx.currentTime, glide = 0): void {
    const gliding = glide > 0 && this.current !== null;
    for (const slot of this.oscs) {
      if (slot) this.rampFreq(slot, midi, when, gliding ? glide : 0);
    }
    this.current = midi;
    this.ampEnv.trigger(this.vca.gain, when, VOICE_PEAK);
    this.filterEnv.trigger(this.filterEnvSource.offset, when, 1);
  }

  /** Re-pitch without retriggering envelopes (mono legato / held-note fallback). */
  glideTo(midi: number, when = this.ctx.currentTime, glide = 0): void {
    for (const slot of this.oscs) {
      if (slot) this.rampFreq(slot, midi, when, this.current !== null ? glide : 0);
    }
    this.current = midi;
  }

  noteOff(when = this.ctx.currentTime): void {
    if (this.current === null) return;
    this.ampEnv.release(this.vca.gain, when);
    this.filterEnv.release(this.filterEnvSource.offset, when);
    this.current = null;
  }

  // --- Per-oscillator setters ---

  setOctave(i: OscIndex, octave: number): void {
    const slot = this.oscs[i];
    if (!slot) return;
    slot.octave = octave;
    if (this.current !== null) {
      this.freqParam(slot).setTargetAtTime(
        this.freqFor(this.current, slot),
        this.ctx.currentTime,
        0.005,
      );
    }
  }

  setDetune(i: OscIndex, cents: number): void {
    this.oscs[i]?.node.parameters
      .get('detune')
      ?.setTargetAtTime(cents, this.ctx.currentTime, 0.01);
  }

  setShape(i: OscIndex, value: number): void {
    this.oscs[i]?.node.parameters
      .get('shape')
      ?.setTargetAtTime(value, this.ctx.currentTime, 0.01);
  }

  setLevel(i: OscIndex, value: number): void {
    this.oscs[i]?.level.gain.setTargetAtTime(value, this.ctx.currentTime, 0.01);
  }

  setDrive(i: OscIndex, value: number): void {
    this.oscs[i]?.drive.setDrive(value);
  }

  // --- Filter / envelope setters ---

  setCutoff(hz: number, when = this.ctx.currentTime): void {
    this.filter.frequency.setTargetAtTime(hz, when, 0.01);
  }

  setResonance(norm: number, when = this.ctx.currentTime): void {
    this.filter.Q.setTargetAtTime(resonanceToQ(norm), when, 0.01);
  }

  setFilterEnvAmount(amount: number, when = this.ctx.currentTime): void {
    this.filterEnvAmount.gain.setTargetAtTime(amount * FILTER_ENV_HZ, when, 0.01);
  }

  setAmpEnv(partial: Partial<ReturnType<ADSR['get']>>): void {
    this.ampEnv.set(partial);
  }

  setFilterEnv(partial: Partial<ReturnType<ADSR['get']>>): void {
    this.filterEnv.set(partial);
  }

  /** The live AudioParam backing a modulatable destination, if any. */
  getModTarget(id: ParamId): AudioParam | null {
    switch (id) {
      case 'filter.cutoff':
        return this.filter.frequency;
      case 'filter.resonance':
        return this.filter.Q;
      default:
        break;
    }
    const m = /^osc([123])\.(detune|shape|level)$/.exec(id);
    if (m) {
      const slot = this.oscs[(Number(m[1]) - 1) as OscIndex];
      if (!slot) return null;
      if (m[2] === 'level') return slot.level.gain;
      return slot.node.parameters.get(m[2]!) ?? null;
    }
    return null;
  }

  kill(when = this.ctx.currentTime): void {
    this.vca.gain.cancelScheduledValues(when);
    this.vca.gain.setValueAtTime(0, when);
    this.filterEnvSource.offset.cancelScheduledValues(when);
    this.filterEnvSource.offset.setValueAtTime(0, when);
    this.current = null;
  }

  private freqParam(slot: OscSlot): AudioParam {
    return slot.node.parameters.get('frequency')!;
  }

  private freqFor(midi: number, slot: OscSlot): number {
    return midiToFreq(midi) * Math.pow(2, slot.octave);
  }

  /** Set a slot's frequency, gliding (constant-ratio = linear in pitch) if asked. */
  private rampFreq(slot: OscSlot, midi: number, when: number, glide: number): void {
    const f = this.freqFor(midi, slot);
    const p = this.freqParam(slot);
    if (glide > 0) {
      if (typeof p.cancelAndHoldAtTime === 'function') p.cancelAndHoldAtTime(when);
      else p.setValueAtTime(Math.max(p.value, 1e-6), when);
      p.exponentialRampToValueAtTime(Math.max(f, 1e-6), when + glide);
    } else {
      p.setValueAtTime(f, when);
    }
  }
}
